import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate, useSearchParams, Navigate } from "react-router-dom";
import { ArrowLeft, Save, Send, Undo2, Link as LinkIcon, Download, ImagePlus, BarChart3, Eye, GripVertical, X, FileUp, FileSignature, History, RotateCcw, DollarSign } from "lucide-react";
import { toast } from "sonner";
import Header from "../components/dashboard/Header";
import { useAuth } from "../context/AuthContext";
import api, { formatApiErrorDetail } from "../lib/api";
import ProposalView from "./ProposalView";
import { exportNodeToPdfPaged } from "../lib/pdf";
import { fmtCurrency } from "../lib/format";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

const inputCls = "w-full bg-brand-page border border-brand-border rounded-md px-3 py-2.5 text-sm text-brand-ink focus:outline-none focus:border-brand-primary transition-colors";
const labelCls = "block text-[10px] uppercase tracking-[0.15em] text-brand-tertiary mb-1.5";
const barBtn = "inline-flex items-center gap-1.5 text-sm px-3 py-2 border border-brand-border rounded-md transition-colors";

function fmtWhen(iso) {
  const d = new Date(iso);
  return isNaN(d.getTime()) ? "" : d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}
function ContactBlock({ title, c }) {
  if (!c || (!c.name && !c.email && !c.phone)) return null;
  return (
    <div className="rounded border border-brand-border/70 p-2.5">
      <p className="text-[10px] uppercase tracking-[0.15em] text-brand-tertiary mb-1">{title}</p>
      {c.name && <p className="text-brand-ink">{c.name}</p>}
      {c.email && <p className="text-brand-muted">{c.email}</p>}
      {c.phone && <p className="text-brand-muted">{c.phone}</p>}
    </div>
  );
}

function OfficePicker({ loc, unselected, onAdd }) {
  const [q, setQ] = useState("");
  const query = q.trim().toLowerCase();
  const matches = query
    ? unselected.filter((o) => {
        const num = String(o.office_number || "").toLowerCase();
        const desks = o.desks != null ? String(o.desks) : "";
        return num.includes(query) || desks === query || desks.includes(query);
      })
    : [];
  return (
    <div className="border-t border-brand-border pt-3" data-testid={`office-picker-${loc}`}>
      <p className="text-[10px] uppercase tracking-[0.15em] text-brand-tertiary mb-1.5">Add from catalog</p>
      <input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        data-testid={`office-search-${loc}`}
        placeholder={`Search ${unselected.length} offices by office # or desks\u2026`}
        className={inputCls}
      />
      {query && (
        <div className="mt-2 max-h-56 overflow-y-auto space-y-1" data-testid={`office-search-results-${loc}`}>
          {matches.length === 0 ? (
            <p className="text-xs text-brand-tertiary py-1">No offices match "{q}".</p>
          ) : (
            matches.slice(0, 25).map((o) => (
              <button key={o.id} onClick={() => { onAdd(o.id); setQ(""); }}
                data-testid={`office-search-pick-${o.office_number}`}
                className="w-full flex items-center gap-3 text-sm text-left py-1.5 px-2 rounded-md hover:bg-brand-hover transition-colors">
                <span className="text-brand-ink">Office {o.office_number}</span>
                <span className="text-brand-tertiary text-xs ml-auto">{o.desks != null ? `${o.desks} desks \u00b7 ` : ""}${o.monthly_price?.toLocaleString()}/mo</span>
              </button>
            ))
          )}
          {matches.length > 25 && (
            <p className="text-[11px] text-brand-tertiary py-1">Showing 25 of {matches.length}. Keep typing to narrow.</p>
          )}
        </div>
      )}
    </div>
  );
}

function changeText(c) {
  if (c.type === "price") {
    const f = c.from != null ? fmtCurrency(c.from) : "—";
    const t = c.to != null ? fmtCurrency(c.to) : "—";
    return `${c.label}: ${f} → ${t}`;
  }
  if (c.type === "field") {
    const f = (c.from ?? "") === "" ? "(empty)" : String(c.from).slice(0, 40);
    const t = (c.to ?? "") === "" ? "(empty)" : String(c.to).slice(0, 40);
    return `${c.label}: “${f}” → “${t}”`;
  }
  return c.label;
}

function HistoryDialog({ pid, open, onOpenChange, onRestored }) {
  const [hist, setHist] = useState(null);
  useEffect(() => {
    if (!open) return;
    setHist(null);
    api.get(`/proposals/${pid}/history`).then(({ data }) => setHist(data.history || [])).catch(() => setHist([]));
  }, [open, pid]);
  const restore = async (v) => {
    if (!window.confirm(`Restore version ${v}? This becomes the current proposal (saved as a new history entry).`)) return;
    try { await api.post(`/proposals/${pid}/versions/${v}/restore`); toast.success(`Restored v${v}`); onOpenChange(false); onRestored(); }
    catch (e) { toast.error("Restore failed", { description: formatApiErrorDetail(e.response?.data?.detail) }); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg bg-brand-surface border-brand-border max-h-[80vh] overflow-y-auto" data-testid="proposal-history-dialog">
        <DialogHeader><DialogTitle className="font-serif text-2xl text-brand-ink">Version history</DialogTitle></DialogHeader>
        {hist === null ? (
          <div className="py-10 flex justify-center"><div className="h-8 w-8 rounded-full border-2 border-brand-border border-t-brand-primary animate-spin" /></div>
        ) : hist.length === 0 ? (
          <p className="text-sm text-brand-muted py-6 text-center">No history yet. Each save creates a version.</p>
        ) : (
          <ol className="relative space-y-5 pl-5 border-l border-brand-border">
            {hist.map((v, idx) => (
              <li key={v.version} className="relative" data-testid={`proposal-version-${v.version}`}>
                <span className="absolute -left-[22px] top-1 h-2.5 w-2.5 rounded-full bg-brand-primary" />
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-sm text-brand-ink">
                      <span className="font-medium">v{v.version}</span>
                      <span className="text-brand-tertiary"> · {v.event}</span>
                    </p>
                    <p className="text-[11px] text-brand-tertiary">{v.saved_by || "—"} · {fmtWhen(v.saved_at)}</p>
                  </div>
                  {idx !== 0 && (
                    <button onClick={() => restore(v.version)} data-testid={`restore-version-${v.version}`}
                      className="inline-flex items-center gap-1.5 text-xs rounded-md px-2.5 py-1.5 border border-brand-border text-brand-muted hover:text-brand-primary hover:border-brand-primary transition-colors">
                      <RotateCcw className="h-3.5 w-3.5" /> Restore
                    </button>
                  )}
                </div>
                <ul className="mt-2 space-y-1">
                  {v.changes.map((c, i) => (
                    <li key={i} className="text-xs text-brand-muted flex gap-1.5">
                      <span className={`mt-1 h-1 w-1 rounded-full shrink-0 ${c.type === "price" ? "bg-brand-gold" : "bg-brand-tertiary"}`} />
                      <span>{changeText(c)}</span>
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ol>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function ProposalEditor() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fromLead = searchParams.get("fromLead");
  const previewRef = useRef(null);
  const dragLoc = useRef(null);
  const dragOff = useRef(null);

  const [loaded, setLoaded] = useState(false);
  const [locations, setLocations] = useState([]);
  const [offices, setOffices] = useState([]);
  const [title, setTitle] = useState("");
  const [clientName, setClientName] = useState("");
  const [accountExec, setAccountExec] = useState("");
  const [validUntil, setValidUntil] = useState("");
  const [intro, setIntro] = useState("");
  const [notes, setNotes] = useState("");
  const [coverUrl, setCoverUrl] = useState(null);
  const [selLocs, setSelLocs] = useState([]);
  const [selOffices, setSelOffices] = useState([]);
  const [hideFp, setHideFp] = useState([]);
  const [status, setStatus] = useState("draft");
  const [shareToken, setShareToken] = useState(null);
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [requests, setRequests] = useState([]);
  const [priceOv, setPriceOv] = useState({});
  const [priceEditId, setPriceEditId] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);

  const load = useCallback(async () => {
    const [{ data: p }, { data: libs }, { data: offs }] = await Promise.all([
      api.get(`/proposals/${id}`), api.get("/proposals/locations"), api.get("/proposals/offices"),
    ]);
    setLocations(libs.locations || []); setOffices(offs.offices || []);
    setTitle(p.title || ""); setClientName(p.client_name || ""); setAccountExec(p.account_exec || "");
    setValidUntil(p.valid_until || ""); setIntro(p.intro || ""); setNotes(p.notes || "");
    setCoverUrl(p.cover_url || null); setSelLocs(p.locations || []); setSelOffices(p.office_ids || []);
    setHideFp(Array.isArray(p.hide_floorplan) ? p.hide_floorplan : []);
    setPriceOv(p.price_overrides || {});
    setStatus(p.status || "draft"); setShareToken(p.share_token); setLoaded(true);
    api.get(`/proposals/${id}/analytics`).then(({ data }) => setAnalytics(data)).catch(() => {});
    api.get(`/proposals/${id}/requests`).then(({ data }) => setRequests(data.requests || [])).catch(() => {});
  }, [id]);

  const canEdit = ["admin", "regional", "community"].includes(user?.role);
  useEffect(() => { if (canEdit) load(); }, [canEdit, load]);

  if (user && !canEdit) return <Navigate to="/locations" replace />;
  if (!loaded) {
    return <div className="min-h-screen bg-brand-page relative z-10"><Header /><div className="flex items-center justify-center h-[70vh]"><div className="h-10 w-10 rounded-full border-2 border-brand-border border-t-brand-primary animate-spin" /></div></div>;
  }

  const libByLoc = Object.fromEntries(locations.map((l) => [l.location, l]));
  const officeLoc = Object.fromEntries(offices.map((o) => [o.id, o.location]));
  const officeById = Object.fromEntries(offices.map((o) => [o.id, o]));
  const toggleLoc = (loc) => setSelLocs((p) => p.includes(loc) ? p.filter((x) => x !== loc) : [...p, loc]);
  const toggleOffice = (oid) => setSelOffices((p) => p.includes(oid) ? p.filter((x) => x !== oid) : [...p, oid]);
  const toggleFp = (loc) => setHideFp((p) => p.includes(loc) ? p.filter((x) => x !== loc) : [...p, loc]);

  const applyOv = (o) => {
    const ov = priceOv[o.id] || {};
    const out = { ...o };
    ["monthly_price", "price_6mo", "price_12mo"].forEach((f) => {
      out["list_" + f] = o[f];
      if (ov[f] != null) out[f] = ov[f];
    });
    out.has_override = Object.keys(ov).length > 0;
    return out;
  };
  const setOverride = (oid, field, raw) => {
    setPriceOv((prev) => {
      const next = { ...prev };
      const cur = { ...(next[oid] || {}) };
      const master = officeById[oid]?.[field];
      const num = raw === "" || raw == null ? null : Number(raw);
      if (num == null || Number.isNaN(num) || num === master) delete cur[field];
      else cur[field] = num;
      if (Object.keys(cur).length === 0) delete next[oid];
      else next[oid] = cur;
      return next;
    });
  };
  const resetOverride = (oid) => setPriceOv((prev) => { const n = { ...prev }; delete n[oid]; return n; });

  const reorder = (arr, from, to) => {
    const next = [...arr];
    const [m] = next.splice(from, 1);
    next.splice(to, 0, m);
    return next;
  };
  const moveLoc = (from, to) => { if (from == null || from === to) return; setSelLocs((p) => reorder(p, from, to)); };
  const moveOffice = (loc, from, to) => {
    if (from == null || from === to) return;
    const locIds = selOffices.filter((id) => officeLoc[id] === loc);
    const reordered = reorder(locIds, from, to);
    const grouped = {};
    selOffices.forEach((id) => { const l = officeLoc[id]; (grouped[l] = grouped[l] || []).push(id); });
    grouped[loc] = reordered;
    setSelOffices(selLocs.flatMap((l) => grouped[l] || []));
  };

  const draft = {
    title: title || "Untitled Proposal", client_name: clientName, intro, notes, valid_until: validUntil,
    account_exec: accountExec, cover_url: coverUrl, locations: selLocs,
    location_blocks: selLocs.map((loc) => {
      const info = libByLoc[loc] || { location: loc, amenities: [], images: [] };
      return {
        location: loc, city: info.city || "", address: info.address || "", description: info.description || "",
        amenities: info.amenities || [], conference_rooms: info.conference_rooms || "", conference_rooms_list: info.conference_rooms_list || [],
        images: hideFp.includes(loc) ? (info.images || []).filter((i) => i.kind !== "floorplan") : (info.images || []),
        offices: selOffices.filter((oid) => officeLoc[oid] === loc).map((oid) => officeById[oid]).filter(Boolean).map(applyOv),
      };
    }),
  };

  const uploadCover = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try { const { data } = await api.post("/proposals/media", fd); setCoverUrl(data.url); toast.success("Cover uploaded"); }
    catch (err) { toast.error("Upload failed", { description: formatApiErrorDetail(err.response?.data?.detail) }); }
  };

  const putLocation = async (loc, images) => {
    const cur = libByLoc[loc] || { location: loc };
    await api.put(`/proposals/locations/${encodeURIComponent(loc)}`, {
      location: loc, address: cur.address || "", city: cur.city || "", description: cur.description || "",
      amenities: cur.amenities || [], conference_rooms: cur.conference_rooms || "", images,
    });
    setLocations((p) => p.map((l) => l.location === loc ? { ...l, images } : l));
  };
  const uploadLocMedia = async (loc, e, kind) => {
    const file = e.target.files?.[0]; if (!file) return;
    const isPdf = file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf");
    const fd = new FormData(); fd.append("file", file);
    const tid = toast.loading(isPdf ? "Converting PDF…" : "Uploading…");
    try {
      const { data } = await api.post("/proposals/media", fd);
      const raw = data.items || [{ id: data.url.split("/").pop(), url: data.url, pdf: data.pdf }];
      const newImgs = raw.map((it) => ({ id: it.id || it.url.split("/").pop(), url: it.url, caption: "", kind, pdf: !!it.pdf }));
      const images = [...((libByLoc[loc] || {}).images || []), ...newImgs];
      await putLocation(loc, images);
      if (kind === "floorplan") setHideFp((p) => p.filter((x) => x !== loc));
      toast.success(`${newImgs.length} ${kind === "floorplan" ? (newImgs.length > 1 ? "floor plan pages" : "floor plan") : "photo"} added`, { id: tid });
    } catch (err) { toast.error("Upload failed", { id: tid, description: formatApiErrorDetail(err.response?.data?.detail) }); }
    finally { e.target.value = ""; }
  };
  const removeLocMedia = async (loc, url) => {
    const images = ((libByLoc[loc] || {}).images || []).filter((i) => i.url !== url);
    await putLocation(loc, images);
  };

  const body = () => ({
    title: title.trim() || "Untitled Proposal", client_name: clientName, intro, notes, valid_until: validUntil,
    account_exec: accountExec, cover_url: coverUrl, locations: selLocs, office_ids: selOffices, hide_floorplan: hideFp,
    price_overrides: priceOv,
  });

  const save = async () => {
    setSaving(true);
    try { await api.put(`/proposals/${id}`, body()); toast.success("Saved"); await load(); }
    catch (err) { toast.error("Save failed", { description: formatApiErrorDetail(err.response?.data?.detail) }); }
    finally { setSaving(false); }
  };
  const publish = async (pub) => {
    try { await api.put(`/proposals/${id}`, body()); await api.post(`/proposals/${id}/publish`, { published: pub });
      toast.success(pub ? "Published" : "Unpublished"); await load(); }
    catch (err) { toast.error("Failed", { description: formatApiErrorDetail(err.response?.data?.detail) }); }
  };
  const copyLink = () => {
    navigator.clipboard.writeText(`${window.location.origin}/p/${shareToken}`);
    toast.success("Public link copied");
  };
  const markHandled = async (rid) => {
    try { await api.post(`/proposals/requests/${rid}/handle`); toast.success("Marked handled"); load(); }
    catch { toast.error("Could not update the request"); }
  };
  const downloadPdf = async () => {
    setDownloading(true);
    try { await exportNodeToPdfPaged(previewRef.current, `${(title || "proposal").replace(/[^a-z0-9]+/gi, "-")}.pdf`); }
    catch { toast.error("Could not generate PDF"); } finally { setDownloading(false); }
  };

  return (
    <div className="min-h-screen bg-brand-page relative z-10">
      <Header />
      <div className="sticky top-0 z-20 bg-brand-page/90 backdrop-blur-xl border-b border-brand-border">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12 h-14 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            {fromLead && (
              <button onClick={() => navigate(`/crm?lead=${fromLead}`)} data-testid="proposal-back-to-deal"
                className="inline-flex items-center gap-1.5 rounded-md bg-brand-gold/15 px-3 py-1.5 text-sm font-medium text-brand-gold transition-colors hover:bg-brand-gold/25">
                <ArrowLeft className="h-4 w-4" /> Back to deal
              </button>
            )}
            <button onClick={() => { const from = new URLSearchParams(window.location.search).get("from"); navigate(from ? `/locations/${encodeURIComponent(from)}` : "/proposals"); }} data-testid="proposal-back" className="inline-flex items-center gap-1.5 text-sm text-brand-muted hover:text-brand-ink"><ArrowLeft className="h-4 w-4" /> {new URLSearchParams(window.location.search).get("from") ? `Back to ${new URLSearchParams(window.location.search).get("from")}` : "All proposals"}</button>
          </div>
          <div className="flex items-center gap-2">
            <span className={`text-[10px] uppercase tracking-[0.15em] px-2 py-1 rounded-full ${status === "published" ? "bg-brand-pos/10 text-brand-pos" : "bg-brand-hover text-brand-tertiary"}`}>{status}</span>
            <button onClick={downloadPdf} disabled={downloading} data-testid="proposal-download" className={`${barBtn} text-brand-muted hover:text-brand-primary disabled:opacity-60`}><Download className="h-4 w-4" /> {downloading ? "…" : "PDF"}</button>
            {shareToken && status === "published" && <button onClick={copyLink} data-testid="proposal-copy-link" className={`${barBtn} text-brand-muted hover:text-brand-primary`}><LinkIcon className="h-4 w-4" /> Link</button>}
            <button onClick={() => setHistoryOpen(true)} data-testid="proposal-history-btn" className={`${barBtn} text-brand-muted hover:text-brand-primary`}><History className="h-4 w-4" /> History</button>
            <button onClick={save} disabled={saving} data-testid="proposal-save" className={`${barBtn} text-brand-muted hover:text-brand-primary disabled:opacity-60`}><Save className="h-4 w-4" /> {saving ? "…" : "Save"}</button>
            {status === "published"
              ? <button onClick={() => publish(false)} data-testid="proposal-unpublish" className={`${barBtn} text-brand-neg border-brand-neg/40`}><Undo2 className="h-4 w-4" /> Unpublish</button>
              : <button onClick={() => publish(true)} data-testid="proposal-publish" className="inline-flex items-center gap-1.5 text-sm px-4 py-2 bg-brand-primary text-brand-page rounded-md hover:bg-brand-primaryHover"><Send className="h-4 w-4" /> Publish</button>}
          </div>
        </div>
      </div>

      <div className="max-w-[1600px] mx-auto px-6 lg:px-12 py-8 grid lg:grid-cols-[minmax(0,420px)_1fr] gap-8">
        {/* Form */}
        <div className="space-y-5">
          {requests.filter((r) => r.status === "new").length > 0 && (
            <div className="bg-brand-gold/10 border border-brand-gold/50 rounded-lg p-5" data-testid="agreement-requests">
              <p className="font-serif text-xl text-brand-ink flex items-center gap-2"><FileSignature className="h-5 w-5 text-brand-gold" /> Agreement requests</p>
              <p className="text-[12px] text-brand-muted mt-1">A prospect selected office(s) and asked for an agreement. Verify the details below, then issue the contract from the lead (Convert → Yardi). Nothing is finalized until the agreement is signed.</p>
              <div className="mt-4 space-y-4">
                {requests.filter((r) => r.status === "new").map((r) => (
                  <div key={r.id} className="rounded-md border border-brand-border bg-brand-page p-4" data-testid={`agreement-request-${r.id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="text-sm font-semibold text-brand-ink">{r.company_name}</p>
                        <p className="text-[11px] text-brand-tertiary">{fmtWhen(r.created_at)}</p>
                      </div>
                      <button onClick={() => markHandled(r.id)} data-testid={`request-handle-${r.id}`}
                        className="shrink-0 text-[12px] rounded-md bg-brand-ink text-brand-page px-3 py-1.5 hover:bg-brand-primary transition-colors">Mark handled</button>
                    </div>
                    <div className="mt-3 space-y-1.5 border-t border-brand-border pt-3">
                      {(r.offices || []).map((o, i) => (
                        <div key={i} className="flex items-center justify-between gap-3 text-[13px]">
                          <span className="text-brand-ink">Office {o.office_number}{o.location ? ` · ${o.location}` : ""} — {o.term_label || o.term}</span>
                          <span className="font-mono text-brand-muted whitespace-nowrap">{o.price != null ? `$${Math.round(Number(o.price)).toLocaleString()}/mo` : "—"}</span>
                        </div>
                      ))}
                    </div>
                    {r.start_date && <p className="mt-2 text-[12px] text-brand-muted">Preferred start: <span className="text-brand-ink">{r.start_date}</span></p>}
                    <div className="mt-3 grid sm:grid-cols-2 gap-2.5">
                      <ContactBlock title="Main contact" c={r.main_contact} />
                      <ContactBlock title="Billing contact" c={r.billing_contact} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="grid grid-cols-1 gap-4 bg-brand-surface border border-brand-border p-5">
            <div><label className={labelCls}>Proposal Title</label><input className={inputCls} value={title} onChange={(e) => setTitle(e.target.value)} data-testid="proposal-title" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className={labelCls}>Client Name</label><input className={inputCls} value={clientName} onChange={(e) => setClientName(e.target.value)} data-testid="proposal-client" /></div>
              <div><label className={labelCls}>Account Exec</label><input className={inputCls} value={accountExec} onChange={(e) => setAccountExec(e.target.value)} data-testid="proposal-exec" /></div>
            </div>
            <div><label className={labelCls}>Valid Until</label><input type="date" className={inputCls} value={validUntil} onChange={(e) => setValidUntil(e.target.value)} data-testid="proposal-valid" /></div>
            <div>
              <label className={labelCls}>Cover Image</label>
              <label className="inline-flex items-center gap-2 text-sm text-brand-primary cursor-pointer">
                <ImagePlus className="h-4 w-4" /> {coverUrl ? "Replace cover" : "Upload cover"}
                <input type="file" accept="image/*" className="hidden" onChange={uploadCover} data-testid="proposal-cover-upload" />
              </label>
            </div>
            <div><label className={labelCls}>Overview / Intro</label><textarea rows={4} className={inputCls} value={intro} onChange={(e) => setIntro(e.target.value)} data-testid="proposal-intro-input" /></div>
          </div>

          <div className="bg-brand-surface border border-brand-border p-5">
            <p className={labelCls}>Locations Offered</p>
            <div className="flex flex-wrap gap-2">
              {locations.map((l) => (
                <button key={l.location} onClick={() => toggleLoc(l.location)} data-testid={`proposal-loc-${l.location}`}
                  className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${selLocs.includes(l.location) ? "bg-brand-primary text-brand-page border-brand-primary" : "border-brand-border text-brand-muted hover:border-brand-borderStrong"}`}>{l.location}</button>
              ))}
            </div>
            {selLocs.length > 1 && (
              <div className="mt-4">
                <p className="text-[10px] uppercase tracking-[0.15em] text-brand-tertiary mb-2">Drag to set display order</p>
                <div className="space-y-1.5" data-testid="proposal-loc-order">
                  {selLocs.map((loc, i) => (
                    <div key={loc} draggable onDragStart={() => (dragLoc.current = i)} onDragOver={(e) => e.preventDefault()} onDrop={() => { moveLoc(dragLoc.current, i); dragLoc.current = null; }}
                      data-testid={`proposal-loc-order-${loc}`} className="flex items-center gap-2 bg-brand-page border border-brand-border rounded-md px-3 py-2 text-sm cursor-grab active:cursor-grabbing">
                      <GripVertical className="h-4 w-4 text-brand-tertiary" />
                      <span className="font-mono text-xs text-brand-gold">{String(i + 1).padStart(2, "0")}</span>
                      <span className="text-brand-ink">{loc}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {selLocs.map((loc) => {
            const locOffices = offices.filter((o) => o.location === loc);
            const selectedIds = selOffices.filter((id) => officeLoc[id] === loc);
            const unselected = locOffices.filter((o) => !selOffices.includes(o.id));
            return (
              <div key={loc} className="bg-brand-surface border border-brand-border p-5" data-testid={`proposal-offices-${loc}`}>
                <div className="mb-4 pb-4 border-b border-brand-border scroll-mt-24" data-testid={`proposal-loc-media-${loc}`}>
                  <p className={`${labelCls} mb-3`}>{loc} — photos &amp; floor plan</p>
                  <div className="flex flex-wrap items-center gap-2.5 mb-3">
                    <label className="inline-flex items-center gap-2 text-sm px-4 py-2 rounded-md border border-brand-border hover:border-brand-primary hover:bg-brand-hover cursor-pointer transition-colors"><ImagePlus className="h-4 w-4" /> Add photo<input type="file" accept="image/*" className="hidden" onChange={(e) => uploadLocMedia(loc, e, "photo")} data-testid={`loc-add-photo-${loc}`} /></label>
                    <label className="inline-flex items-center gap-2 text-sm font-medium px-4 py-2 rounded-md border border-brand-primary text-brand-primary hover:bg-brand-primary hover:text-brand-page cursor-pointer transition-colors"><FileUp className="h-4 w-4" /> Upload floor plan (PDF)<input type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => uploadLocMedia(loc, e, "floorplan")} data-testid={`loc-add-floorplan-${loc}`} /></label>
                  </div>
                  {((libByLoc[loc] || {}).images || []).length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {(libByLoc[loc].images || []).map((im) => (
                        <div key={im.url} className="relative">
                          {im.pdf ? (
                            <div className="h-16 w-16 border border-brand-border bg-brand-hover flex flex-col items-center justify-center text-brand-tertiary"><FileUp className="h-5 w-5" /><span className="text-[8px] mt-0.5">PDF</span></div>
                          ) : (
                            <img src={im.url.startsWith("http") ? im.url : `${process.env.REACT_APP_BACKEND_URL}${im.url}`} alt="" className="h-16 w-16 object-cover border border-brand-border" />
                          )}
                          <span className="absolute bottom-0 left-0 text-[8px] uppercase bg-brand-ink/70 text-brand-page px-1">{im.kind === "floorplan" ? "plan" : "photo"}</span>
                          <button onClick={() => removeLocMedia(loc, im.url)} className="absolute -top-2 -right-2 bg-brand-neg text-white rounded-full h-5 w-5 flex items-center justify-center text-xs leading-none">×</button>
                        </div>
                      ))}
                    </div>
                  )}
                  {(() => {
                    const hasFp = ((libByLoc[loc] || {}).images || []).some((i) => i.kind === "floorplan");
                    return (
                      <label className={`mt-3 flex items-center gap-2 text-sm ${hasFp ? "text-brand-ink cursor-pointer" : "text-brand-tertiary cursor-not-allowed"}`}>
                        <input type="checkbox" checked={hasFp && !hideFp.includes(loc)} disabled={!hasFp} onChange={() => toggleFp(loc)}
                          data-testid={`loc-include-floorplan-${loc}`} className="accent-brand-primary h-4 w-4" />
                        Include floor plan in this proposal{hasFp ? "" : " — upload one above to enable"}
                      </label>
                    );
                  })()}
                </div>
                <p className={labelCls}>{loc} — offices in this proposal</p>
                {locOffices.length === 0 ? (
                  <p className="text-xs text-brand-tertiary">No offices in the catalog for {loc}. Add them under Proposals → Office Catalog.</p>
                ) : (
                  <>
                    {selectedIds.length > 0 && (
                      <div className="space-y-1.5 mb-3" data-testid={`proposal-office-order-${loc}`}>
                        {selectedIds.map((id, i) => {
                          const o = officeById[id]; if (!o) return null;
                          const ov = priceOv[id] || {};
                          const editing = priceEditId === id;
                          return (
                            <div key={id} data-testid={`proposal-office-ordered-${o.office_number}`} className="bg-brand-page border border-brand-border rounded-md">
                              <div draggable onDragStart={() => (dragOff.current = i)} onDragOver={(e) => e.preventDefault()} onDrop={() => { moveOffice(loc, dragOff.current, i); dragOff.current = null; }}
                                className="flex items-center gap-2 px-3 py-2 text-sm cursor-grab active:cursor-grabbing">
                                <GripVertical className="h-4 w-4 text-brand-tertiary shrink-0" />
                                <span className="text-brand-ink">Office {o.office_number}</span>
                                <span className="text-brand-tertiary text-xs">
                                  {o.desks != null ? `${o.desks} desks · ` : ""}
                                  {ov.monthly_price != null
                                    ? <><span className="line-through mr-1">${o.monthly_price?.toLocaleString()}</span><span className="text-brand-primary">${ov.monthly_price.toLocaleString()}/mo</span></>
                                    : `$${o.monthly_price?.toLocaleString()}/mo`}
                                </span>
                                <button onClick={() => setPriceEditId(editing ? null : id)} title="Edit pricing" data-testid={`edit-price-${o.office_number}`}
                                  className={`ml-auto p-1 transition-colors ${ov && Object.keys(ov).length ? "text-brand-primary" : "text-brand-tertiary hover:text-brand-primary"}`}><DollarSign className="h-4 w-4" /></button>
                                <button onClick={() => toggleOffice(id)} className="text-brand-tertiary hover:text-brand-neg"><X className="h-4 w-4" /></button>
                              </div>
                              {editing && (
                                <div className="px-3 pb-3 pt-1 border-t border-brand-border" data-testid={`price-editor-${o.office_number}`}>
                                  <div className="flex items-center justify-between mb-2">
                                    <p className="text-[10px] uppercase tracking-[0.15em] text-brand-tertiary">Negotiated price — blank = catalog rate</p>
                                    {Object.keys(ov).length > 0 && (
                                      <button onClick={() => resetOverride(id)} data-testid={`reset-price-${o.office_number}`}
                                        className="inline-flex items-center gap-1 text-[11px] text-brand-tertiary hover:text-brand-neg"><RotateCcw className="h-3 w-3" /> Reset</button>
                                    )}
                                  </div>
                                  <div className="grid grid-cols-3 gap-2">
                                    {[["monthly_price", "Monthly"], ["price_6mo", "6-month"], ["price_12mo", "12-month"]].map(([f, lbl]) => (
                                      <div key={f}>
                                        <label className="text-[9px] uppercase tracking-[0.12em] text-brand-tertiary block mb-1">{lbl}</label>
                                        <input type="number" value={ov[f] ?? ""} placeholder={o[f] != null ? String(o[f]) : "—"}
                                          onChange={(e) => setOverride(id, f, e.target.value)} data-testid={`price-input-${o.office_number}-${f}`}
                                          className="w-full bg-brand-surface border border-brand-border rounded px-2 py-1.5 text-sm text-brand-ink focus:outline-none focus:border-brand-primary" />
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                    {unselected.length > 0 && (
                      <OfficePicker loc={loc} unselected={unselected} onAdd={toggleOffice} />
                    )}
                  </>
                )}
              </div>
            );
          })}

          <div className="bg-brand-surface border border-brand-border p-5">
            <label className={labelCls}>Notes &amp; Inclusions</label>
            <textarea rows={5} className={inputCls} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="proposal-notes-input" />
          </div>

          {analytics && (
            <div className="bg-brand-surface border border-brand-border p-5" data-testid="proposal-analytics">
              <p className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-brand-tertiary mb-4"><BarChart3 className="h-4 w-4" /> Engagement</p>
              <div className="flex items-center gap-6 mb-4">
                <div>
                  <p className="font-mono text-3xl text-brand-ink" data-testid="analytics-opens">{analytics.opens}</p>
                  <p className="text-[11px] text-brand-tertiary uppercase tracking-[0.12em]">Link opens</p>
                </div>
                {analytics.last_opened && (
                  <div>
                    <p className="text-sm text-brand-muted">{new Date(analytics.last_opened).toLocaleString()}</p>
                    <p className="text-[11px] text-brand-tertiary uppercase tracking-[0.12em]">Last opened</p>
                  </div>
                )}
              </div>
              {analytics.offices?.length > 0 ? (
                <div>
                  <p className="text-[11px] text-brand-tertiary uppercase tracking-[0.12em] mb-2">Most-viewed offices</p>
                  <div className="space-y-1.5">
                    {analytics.offices.map((o) => {
                      const max = analytics.offices[0].views || 1;
                      return (
                        <div key={o.office_number} className="flex items-center gap-3" data-testid={`analytics-office-${o.office_number}`}>
                          <span className="text-sm text-brand-ink w-20 shrink-0">Office {o.office_number}</span>
                          <div className="flex-1 h-2 bg-brand-hover rounded-full overflow-hidden"><div className="h-full bg-brand-gold" style={{ width: `${(o.views / max) * 100}%` }} /></div>
                          <span className="inline-flex items-center gap-1 text-xs font-mono text-brand-muted w-12 justify-end"><Eye className="h-3 w-3" /> {o.views}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <p className="text-xs text-brand-tertiary">{analytics.opens > 0 ? "No office views tracked yet." : "Publish and share the link to start tracking engagement."}</p>
              )}
            </div>
          )}
        </div>

        {/* Preview */}
        <div className="border border-brand-border bg-brand-page overflow-hidden">
          <ProposalView ref={previewRef} proposal={draft} />
        </div>
      </div>
      <HistoryDialog pid={id} open={historyOpen} onOpenChange={setHistoryOpen} onRestored={load} />
    </div>
  );
}
